import React, { useEffect, useState } from 'react';
import s from './chatStyles';
import TagPicker from './TagPicker';
import { fmtTime, fmtDate, timeAgo } from './format';
import { Hand, Undo2, Info, Bot, PenLine, UserRound, Trash2, FileText, AlertTriangle, MessagesSquare, Hourglass, Pencil, Check, X, ArrowRightLeft, History, Megaphone, Archive, ArchiveRestore } from 'lucide-react';
import { statusInfo, ORIGIN } from './status';
import AuthImage, { Unavailable } from '../media/AuthImage';

/**
 * Encabezado y mensajes de la conversación seleccionada.
 */
export default function ConversationView({
  conv, messages, loading, isAdmin, currentUserId, canClose, users, onAssign, onAttend, attending, onClose, onDeleteMessage, messagesEndRef,
  allTags = [], tagPickerOpen, onToggleTagPicker, onAddTag, onRemoveTag, onSaveName,
  onTransfer, onSetPreferred, onArchive,
}) {
  const saraActive = ['bot', 'closed'].includes(conv.status);
  const st = statusInfo(conv.status);

  // Agrupar mensajes por fecha
  const grouped = [];
  let lastDate = null;
  messages.forEach(msg => {
    const d = fmtDate(msg.created_at);
    if (d !== lastDate) { grouped.push({ _type: 'date', label: d }); lastDate = d; }
    grouped.push(msg);
  });

  return (
    <>
      {/* Encabezado de la conversación */}
      <div style={s.chatHeader}>
        <div style={{ ...s.chatCanalBadge, background: st.soft, color: st.color }}>
          <st.Icon size={20} />
        </div>
        <div style={s.chatHeaderMid}>
          <ContactName conv={conv} onSave={onSaveName} />
          <div style={s.chatHeaderSub} title={conv.session_id}>
            {conv.contact_name ? `${conv.user_id} · ` : ''}WhatsApp · {conv.message_count} mensajes{conv.origin ? ` · ${ORIGIN[conv.origin] || conv.origin}` : ''}
          </div>
          {onToggleTagPicker && (
            <div style={{ marginTop: 6 }}>
              <TagPicker conv={conv} allTags={allTags} open={tagPickerOpen}
                onToggle={onToggleTagPicker} onAdd={onAddTag} onRemove={onRemoveTag} />
            </div>
          )}
        </div>
        <div style={s.headerActions}>
          <div style={{ ...s.modoTag, color: st.color, background: st.soft, border: `1px solid ${st.border}` }}
            title={conv.origin ? ORIGIN[conv.origin] || conv.origin : undefined}>
            <st.Icon size={13} /> {st.label}
            {conv.status === 'assigned' && conv.assigned_user && ` · ${conv.assigned_user.name}`}
            {conv.status === 'queued' && conv.queued_at && ` · hace ${timeAgo(conv.queued_at)}`}
          </div>
          {isAdmin && conv.status !== 'assigned' && (
            <button className="ui-primary" style={s.primaryBtn} onClick={() => onAssign(conv.session_id, currentUserId)}
              title={saraActive ? 'Pausa a SARA y te asigna la conversación' : 'Te asigna el caso'}>
              <Hand size={14} /> Atender yo
            </button>
          )}
          {/* Asesor técnico mirando un chat de SARA: lo toma en cualquier momento */}
          {!isAdmin && saraActive && onAttend && (
            <button className="ui-primary" style={s.primaryBtn} onClick={() => onAttend(conv.session_id)} disabled={attending}
              title="Pausa a SARA y te asigna la conversación">
              <Hand size={14} /> {attending ? 'Tomando…' : 'Atender yo'}
            </button>
          )}
          {isAdmin && users?.length > 0 && (
            <select
              style={s.assignSelect}
              value=""
              onChange={e => e.target.value && onAssign(conv.session_id, Number(e.target.value))}
              title="Asignar o reasignar"
              aria-label={conv.status === 'assigned' ? 'Reasignar a' : 'Asignar a'}
            >
              <option value="">{conv.status === 'assigned' ? 'Reasignar a…' : 'Asignar a…'}</option>
              {users
                .filter(u => !(conv.status === 'assigned' && u.id === conv.assigned_user?.id))
                .map(u => (
                  // Sin "Recibe casos" no se le pueden asignar casos (salvo tomarlo uno mismo)
                  <option key={u.id} value={u.id} disabled={!u.receives_assignments && u.id !== currentUserId}>
                    {u.name}{u.role === 'admin' ? ' (admin)' : ''} · {u.assigned_count} abiertas
                    {!u.receives_assignments && u.id !== currentUserId ? ' · no recibe casos' : ''}
                  </option>
                ))}
            </select>
          )}
          {canClose && conv.status === 'assigned' && onTransfer && (
            <button className="ui-hover" style={s.actionBtn} onClick={() => onTransfer(conv)} title="Pasar el caso a un compañero">
              <ArrowRightLeft size={14} /> Transferir
            </button>
          )}
          {canClose && ['assigned', 'queued'].includes(conv.status) && (
            <button className="ui-hover" style={s.actionBtn} onClick={() => onClose(conv.session_id)} title="Cierra el caso y SARA vuelve a atender">
              <Undo2 size={14} /> Cerrar y devolver a SARA
            </button>
          )}
          {/* Personal: solo saca el chat de la bandeja; vuelve cuando el cliente escribe */}
          {onArchive && (
            <button className="ui-hover" style={s.actionBtn} onClick={() => onArchive(conv.session_id, !conv.archived)}
              title={conv.archived ? 'Devolver el chat a tu bandeja' : 'Sacar el chat de tu bandeja; vuelve cuando el cliente escriba'}>
              {conv.archived ? <><ArchiveRestore size={14} /> Desarchivar</> : <><Archive size={14} /> Archivar</>}
            </button>
          )}
        </div>
      </div>

      {conv.transfer && conv.status === 'assigned' && (
        <div style={s.transferBox}>
          <ArrowRightLeft size={16} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>Transferido por {conv.transfer.by?.name || 'un compañero'}</strong>
            <span style={{ color: 'var(--text-3)' }}> · hace {timeAgo(conv.transfer.at)}</span>
            {conv.transfer.note && <div style={{ color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>{conv.transfer.note}</div>}
          </div>
        </div>
      )}

      <PreferredAgent conv={conv} isAdmin={isAdmin} users={users} onSetPreferred={onSetPreferred} />

      {(conv.escalation_summary || conv.escalation_reason) && (
        <div style={s.escalationBox}>
          <Info size={16} color="var(--warning)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>{conv.origin ? ORIGIN[conv.origin] || conv.origin : 'Escalación'}</strong>
            {conv.escalation_reason && <> · {conv.escalation_reason}</>}
            {conv.escalation_summary && <div style={{ color: 'var(--text-2)' }}>{conv.escalation_summary}</div>}
          </div>
        </div>
      )}

      {/* Mensajes */}
      <div style={s.messages}>
        {loading && (
          <div style={s.loadingMsgs}>Cargando historial...</div>
        )}
        {grouped.map((item, i) => {
          if (item._type === 'date') {
            return <DateSep key={`d-${i}`} label={item.label} />;
          }
          // El backend ya marca como 'asesor' los mensajes enviados desde el portal
          const isUser = item.type === 'human' && !item.content?.startsWith('[ASESOR]');
          const isAI = item.type === 'ai';
          const failed = item.status === 'failed';
          // Limpiar prefijos que vienen de n8n/helpers
          const text = (item.content || '')
            .replace(/^Texto del mensaje:\s*/i, '')
            .replace(/^\[ASESOR\]\s*/, '');
          const who = isUser
            ? { label: 'Cliente', Icon: UserRound, color: 'var(--text-2)' }
            : isAI
              ? { label: 'SARA', Icon: Bot, color: 'var(--accent-text)' }
              : { label: item.author?.name || 'Asesor', Icon: PenLine, color: 'var(--success)' };

          return (
            <div key={item.id || (item.failed_id ? `f-${item.failed_id}` : i)} className="ui-reveal"
              style={{ ...s.msgRow, justifyContent: isUser ? 'flex-start' : 'flex-end' }}>
              {isUser && (
                <div style={{ ...s.avatar, background: 'var(--surface-3)', color: 'var(--text-2)' }}><UserRound size={15} /></div>
              )}
              <div style={{
                ...s.bubble,
                ...(isUser ? s.bubbleUser : isAI ? s.bubbleAI : s.bubbleAsesor),
                ...(failed ? s.bubbleFailed : {}),
              }}>
                <div style={{ ...s.bubbleWho, color: who.color }}>
                  <who.Icon size={12} strokeWidth={2.4} /> {who.label}
                  {item.template && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 4, color: 'var(--text-3)' }}><FileText size={11} /> {item.template}</span>}
                </div>
                {item.image && (
                  <div style={{ margin: '4px 0 6px' }}>
                    {item.image.available
                      ? <AuthImage url={item.image.url} alt={item.image.title || 'Imagen enviada'} />
                      : <Unavailable />}
                    {item.image.source === 'promotion' && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>
                        <Megaphone size={11} /> Promoción
                      </div>
                    )}
                  </div>
                )}
                {text && <div style={s.bubbleTxt}>{text}</div>}
                {failed && (
                  <div style={{ ...s.failedNote, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <AlertTriangle size={13} /> No enviado: {item.error || 'error de WhatsApp'}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 5 }}>
                  <div style={s.bubbleTs}>{fmtTime(item.created_at)}</div>
                  {isAdmin && item.id && (
                    <button
                      className="ui-reveal-target"
                      onClick={() => onDeleteMessage(conv.session_id, item.id)}
                      style={s.deleteMsgBtn}
                      title="Borrar mensaje"
                      aria-label="Borrar mensaje"
                    ><Trash2 size={12} /></button>
                  )}
                </div>
              </div>
              {!isUser && (
                <div style={{ ...s.avatar, background: isAI ? 'var(--accent-soft)' : 'var(--success-soft)', color: isAI ? 'var(--accent-text)' : 'var(--success)' }}>
                  {isAI ? <Bot size={15} /> : <PenLine size={14} />}
                </div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    </>
  );
}

/**
 * Nombre del contacto con edición en línea. Vacío = vuelve al nombre de Zoho.
 * Solo se muestra el lápiz a quien puede cambiarlo (onSave presente).
 */
function ContactName({ conv, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setEditing(false); setError(''); }, [conv.session_id]);

  const start = () => { setDraft(conv.contact_name_source === 'manual' ? conv.contact_name : conv.contact_name || ''); setError(''); setEditing(true); };
  async function save() {
    setSaving(true);
    try {
      await onSave(conv.session_id, draft.trim());
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div>
        <form style={s.nameEdit} onSubmit={e => { e.preventDefault(); save(); }}>
          <input style={s.nameInput} value={draft} onChange={e => setDraft(e.target.value)} maxLength={80} autoFocus
            placeholder="Nombre del contacto" aria-label="Nombre del contacto"
            onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }} />
          <button type="submit" className="ui-primary" style={{ ...s.primaryBtn, padding: '6px 9px' }} disabled={saving} aria-label="Guardar nombre"><Check size={15} /></button>
          <button type="button" className="ui-hover" style={{ ...s.actionBtn, padding: '6px 9px' }} onClick={() => setEditing(false)} aria-label="Cancelar"><X size={15} /></button>
        </form>
        <div style={{ ...s.chatHeaderSub, color: error ? 'var(--danger)' : 'var(--text-3)' }}>
          {error || (conv.contact_name_source === 'zoho' ? 'Déjalo vacío para usar el nombre de Zoho.' : 'Se guarda en el portal (no cambia Zoho).')}
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <div style={{ ...s.chatHeaderName, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.contact_name || conv.user_id}</div>
      {conv.contact_name_source === 'zoho' && <span style={s.sourceTag} title="Nombre del lead en Zoho">Zoho</span>}
      {conv.contact_name_source === 'manual' && <span style={s.sourceTag} title="Nombre puesto desde el portal">Portal</span>}
      {onSave && (
        <button className="ui-hover" onClick={start} style={{ ...s.deleteMsgBtn, padding: 4, borderRadius: 6 }}
          title={conv.contact_name ? 'Cambiar nombre' : 'Ponerle nombre'} aria-label={conv.contact_name ? 'Cambiar nombre' : 'Ponerle nombre'}>
          <Pencil size={14} />
        </button>
      )}
      {!conv.contact_name && onSave && (
        <button className="ui-hover" onClick={start} style={{ ...s.addTagBtn, cursor: 'pointer', background: 'none' }}>+ nombre</button>
      )}
    </div>
  );
}

/**
 * Asesor anterior (punto 5): si SARA vuelve a escalar antes de la fecha, el caso
 * le llega a él. El admin lo cambia o lo quita.
 */
function PreferredAgent({ conv, isAdmin, users, onSetPreferred }) {
  const p = conv.preferred_user;
  if (!p) return null;
  const until = p?.until ? new Date(p.until).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', timeZone: 'America/Bogota' }) : null;
  return (
    <div style={s.preferredBox}>
      <History size={14} color="var(--text-3)" style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>
        Asesor anterior: <strong>{p.name || 'asesor'}</strong>
        {until ? <span style={{ color: 'var(--text-3)' }}> · si vuelve a escalar antes del {until}, le llega a él</span> : null}
      </span>
      {isAdmin && onSetPreferred && (
        <select style={{ ...s.assignSelect, padding: '4px 8px', fontSize: 12 }} value=""
          onChange={e => { const v = e.target.value; if (v) onSetPreferred(conv.session_id, v === 'none' ? null : Number(v)); }}
          aria-label="Cambiar asesor anterior">
          <option value="">Cambiar…</option>
          <option value="none">Quitar</option>
          {/* El asesor anterior solo aplica a los del balanceador */}
          {(users || []).filter(u => u.receives_assignments && u.agent_type === 'balancer' && u.id !== p.id).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      )}
    </div>
  );
}

function DateSep({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 8px' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span style={{ fontSize: 11, color: 'var(--text-3)', background: 'var(--surface-2)', padding: '3px 12px', borderRadius: 20, border: '1px solid var(--border)' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

export function EmptyState() {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
      <div style={{ width: 72, height: 72, borderRadius: 22, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <MessagesSquare size={34} strokeWidth={1.8} />
      </div>
      <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text)' }}>Selecciona una conversación</div>
      <div style={{ fontSize: 13.5, color: 'var(--text-2)', maxWidth: 340 }}>Elige un chat de la lista para ver el historial y responder.</div>
    </div>
  );
}

/**
 * Caso en cola visto por un asesor con V1: debe tomarlo para ver el chat.
 */
export function QueuedPanel({ conv, onTake, taking }) {
  return (
    <div style={s.queuedPanel}>
      <div style={{ width: 64, height: 64, borderRadius: 20, background: 'var(--warning-soft)', color: 'var(--warning)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Hourglass size={30} /></div>
      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>Caso en cola · {conv.user_id}</div>
      {conv.escalation_reason && <div style={{ color: 'var(--warning)', fontSize: 13 }}>{conv.escalation_reason}</div>}
      {conv.escalation_summary && <div style={{ color: 'var(--text-2)', fontSize: 13, maxWidth: 480 }}>{conv.escalation_summary}</div>}
      <button className="ui-primary" style={s.takeBtn} onClick={() => onTake(conv.session_id)} disabled={taking}>
        <Hand size={16} />
        {taking ? 'Tomando…' : 'Tomar este caso'}
      </button>
    </div>
  );
}
